import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

/**
 * FeatureListingButton — opens a modal explaining the featured slot & duration,
 * then redirects to Stripe Checkout via /api/platform/featured/checkout.
 * Rendered on a seller's own note page and on the admin listings tab.
 */
export default function FeatureListingButton({ noteId, className = "", size = "sm", label = "Feature this Listing" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/platform/featured/config").then((r) => setCfg(r.data)).catch(() => setCfg(null));
  }, [open]);

  const start = async () => {
    if (!user) { toast.error("Please sign in first"); return; }
    setBusy(true);
    try {
      const r = await api.post("/platform/featured/checkout", {
        note_id: noteId, origin_url: window.location.origin,
      });
      if (r.data.url) { window.location.href = r.data.url; return; }
      throw new Error("No checkout URL");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start checkout");
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        size={size}
        className={`rounded-none uppercase text-xs ${className}`}
        data-testid={`feature-btn-${noteId}`}
      >
        {label}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-md" data-testid="feature-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Feature this Listing</DialogTitle>
          </DialogHeader>
          {!cfg ? <div className="text-muted-foreground text-sm">Loading…</div> : (
            <div className="space-y-3 text-sm">
              {!cfg.enabled ? (
                <div className="text-red-600" data-testid="feature-disabled">
                  Featured listings are temporarily unavailable.
                </div>
              ) : (
                <>
                  <p className="text-muted-foreground">
                    Featured listings appear at the top of the marketplace rail with a highlighted badge. Newest paid slot rotates to position #1.
                  </p>
                  <div className="border border-border p-4 grid grid-cols-2 gap-3">
                    <div>
                      <div className="font-overline text-[10px] text-muted-foreground">Price</div>
                      <div className="font-mono-data text-2xl" data-testid="feature-price">{fmtMoney(cfg.price_usd)}</div>
                    </div>
                    <div>
                      <div className="font-overline text-[10px] text-muted-foreground">Duration</div>
                      <div className="font-mono-data text-2xl" data-testid="feature-duration">{cfg.duration_days}d</div>
                    </div>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Payment is processed by Stripe. This charge is a platform fee — it is NOT connected to any note purchase, settlement, or seller payout.
                  </p>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-none uppercase" onClick={start} disabled={busy || !cfg?.enabled} data-testid="feature-start-checkout">
              {busy ? "Redirecting…" : "Pay & Feature"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
